// src/pages/public/Industries/IndustriesPage.jsx
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FaArrowRight } from 'react-icons/fa6'

import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { fetchPageBySlug } from '../../../redux/slices/cmsSlice.js'
import { fetchIndustries } from '../../../redux/slices/industriesSlice.js'

import BuildSolution from '../../../components/common/Common/BuildSolution/BuildSolution.jsx'

import SEO from '../../../components/seo/SEO.jsx'
import { LazyImage } from '../../../components/common/LazyImage.jsx'
import EmptyState from '../../../components/common/EmptyState.jsx'
import Cards from '../../../components/common/Cards.jsx'

import './IndustriesPage.scss'

export default function IndustriesPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { currentPage: industriesPage, loading: pageLoading } = useAppSelector(
    (state) => state.cms
  )
  const { industries, loading: industriesLoading } = useAppSelector(
    (state) => state.industries
  )

  useEffect(() => {
    dispatch(fetchPageBySlug('industries'))
    dispatch(fetchIndustries())
  }, [dispatch])

  const getSectionData = (sectionKey) => {
    return industriesPage?.sections?.find((section) => section.key === sectionKey)
      ?.data
  }

  const heroSection = getSectionData('hero')
  const cardSection = getSectionData('cardsSection')
  const industriesListSection = getSectionData('industriesList')

  if (pageLoading && !industriesPage) {
    return (
      <>
        <SEO title="Industries" />
        <div className="industries-page-root">
          <section className="minimal-hero-section-industries">
            <div className="minimal-hero-section skeleton-hero" />
          </section>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title={industriesPage?.seoTitle || 'Industries'}
        description={industriesPage?.seoDescription}
      />

      <div className="industries-page-root">
        {/* =========================
            HERO – v1 minimal hero
           ========================= */}
        <section className="minimal-hero-section-industries">
          <div className="minimal-hero-section">
            <div className="minimal-hero-container">
              {/* Center Image (from CMS heroSection.imageUrl) */}
              {heroSection?.imageUrl && (
                <motion.div
                  className="center-image-container"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <LazyImage
                    src={heroSection.imageUrl}
                    alt={heroSection.heading || 'Industry Icon'}
                    className="center-image"
                    placeholder={<div className="center-image skeleton-bg" />}
                  />
                </motion.div>
              )}

              {/* Heading */}
              <motion.h1
                className="minimal-heading"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="heading-line">
                  {(heroSection?.heading || 'Industry Solutions').toUpperCase()}
                </span>
              </motion.h1>

              {/* Subheading */}
              <motion.p
                className="minimal-subheading"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {heroSection?.subheading ||
                  'Tailored digital solutions for your industry-specific challenges.'}
              </motion.p>
            </div>
          </div>
        </section>

        {/* =========================
            INDUSTRIES LIST – v1 card layout
           ========================= */}
        <section className="industries-section-list">
          <div className="industries">
            <section className="industries-listing">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {(
                  industriesListSection?.heading ||
                  'Industries We Serve'
                ).toUpperCase()}
              </motion.h2>

              {industriesLoading ? (
                <div className="industry-cards">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="industry-card skeleton-card">
                      <div className="image-container">
                        <div className="skeleton-bg" />
                      </div>
                      <div className="content">
                        <div className="skeleton-line skeleton-line-lg" />
                        <div className="skeleton-line" />
                        <div className="skeleton-line short" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : industries.length > 0 ? (
                <div className="industry-cards">
                  {industries.map((industry, index) => {
                    const imageSrc =
                      industry.cardImageUrl ||
                      industry.hero?.imageUrl ||
                      industry.imageUrl ||
                      industry.image

                    const title = industry.name || industry.heading_1
                    const description =
                      industry.shortDescription ||
                      industry.hero?.subheading ||
                      industry.sub_heading_1

                    const bulletList =
                      industry.tags ||
                      []

                    return (
                      <motion.div
                        key={industry._id || index}
                        className="industry-card"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        whileHover={{ y: -10, transition: { duration: 0.2 } }}
                      >
                        {/* Left image */}
                        <div className="image-container">
                          {imageSrc ? (
                            <LazyImage
                              src={imageSrc}
                              alt={title}
                              className="industry-main-image"
                              placeholder={
                                <div className="industry-main-image skeleton-bg" />
                              }
                            />
                          ) : (
                            <div className="industry-main-image skeleton-bg" />
                          )}
                        </div>

                        {/* Right content */}
                        <div className="content">
                          <h3>{title}</h3>

                          {description && <p>{description}</p>}

                          {Array.isArray(bulletList) && bulletList.length > 0 && (
                            <ul>
                              {bulletList.map((item, itemIndex) => (
                                <li key={itemIndex}>{item}</li>
                              ))}
                            </ul>
                          )}

                          <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={() =>
                              navigate(`/industries/${industry.slug}`)
                            }
                          >
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                              }}
                            >
                              View Solutions <FaArrowRight />
                            </span>
                          </motion.button>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              ) : (
                <EmptyState
                  title="No Industries Available"
                  message="We're currently updating our industry solutions. Please check back soon."
                />
              )}
            </section>
          </div>
        </section>

        <BuildSolution />

        {/* =========================
            INDUSTRY BENEFITS / CARDS
           ========================= */}
        <section className="industries-extra-section">
          <div className="industries-extra-container">
            {cardSection?.capsuleText && (
              <span className="centered-capsule">
                {cardSection.capsuleText}
              </span>
            )}

            {cardSection?.heading && (
              <h2 className="extra-heading">{cardSection.heading?.toUpperCase()}</h2>
            )}

            <Cards />
          </div>
        </section>
      </div>
    </>
  )
}
